import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contactStepSchema, type ContactStepData } from "@/lib/intake-schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "wouter";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Clock,
  ShieldCheck,
  ArrowRight,
  LogIn,
  User,
  Building2,
  Mail,
  MessageSquareText,
} from "lucide-react";
import { TurnstileWidget } from "@/components/security/turnstile-widget";
import { intakeStackedPrimaryCtaClassName } from "@/lib/intake-cta";
import { reportClientError } from "@/lib/queryClient";

interface ContactStepProps {
  onSubmit: (data: ContactStepData, turnstileToken: string | null) => Promise<void>;
  initialData: ContactStepData | null;
  isAuthenticated?: boolean;
  turnstileSiteKey?: string;
  turnstileRequired: boolean;
  turnstileConfigured: boolean;
}

export function ContactStep({
  onSubmit,
  initialData,
  isAuthenticated = false,
  turnstileSiteKey,
  turnstileRequired,
  turnstileConfigured,
}: ContactStepProps) {
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<ContactStepData>({
    resolver: zodResolver(contactStepSchema),
    defaultValues: initialData || {
      fullName: "",
      email: "",
      company: "",
      websiteUrl: "",
      phone: "",
    },
  });

  const handleSubmit = async (data: ContactStepData) => {
    setSubmitError("");

    if (turnstileRequired && !turnstileConfigured) {
      setSubmitError("Verification is temporarily unavailable. Please try again later.");
      return;
    }

    if ((turnstileRequired || turnstileSiteKey) && !turnstileToken) {
      setSubmitError("Please complete the verification step.");
      return;
    }

    setSubmitting(true);

    try {
      await onSubmit(data, turnstileToken);
    } catch (error) {
      reportClientError(error, {
        feature: "intake",
        step: "contact",
      });
      const message = error instanceof Error ? error.message : String(error);
      setSubmitError(
        message.includes("temporarily unavailable")
          ? "Verification is temporarily unavailable. Please try again later."
          : message.includes("Please complete the verification step")
          ? "Please complete the verification step."
          : message.includes("Verification")
            ? "Verification failed. Please try again."
            : "Something went wrong. Please try again.",
      );
      setTurnstileResetKey((value) => value + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Hero */}
      <div className="mb-10">
        <p className="text-sm font-medium text-primary mb-2 tracking-wide uppercase">
          Service Request
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold mb-3 font-heading">
          How can we help you today?
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl">
          Tell us what you need and we’ll prepare the request, confirm whether
          it is covered by your service, and guide the next step clearly.
        </p>
      </div>

      {/* How it works */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        {[
          {
            icon: MessageSquareText,
            title: "Clear request capture",
            description:
              "Share the essentials once. We use that to prepare an internal summary for the right team.",
            accent: "bg-primary/10 text-primary",
          },
          {
            icon: ShieldCheck,
            title: "Service coverage first",
            description:
              "We check whether the work sits inside your existing plan before asking you to approve anything.",
            accent: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
          },
          {
            icon: Clock,
            title: "Straightforward next steps",
            description:
              "Small requests move quickly, and larger work is quoted clearly before anything starts.",
            accent: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
          },
        ].map((item) => (
          <Card
            key={item.title}
            className="border-border bg-card hover-elevate transition-all duration-200"
          >
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={`w-9 h-9 rounded-lg ${item.accent} flex items-center justify-center shrink-0`}
                >
                  <item.icon className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-semibold font-heading">
                  {item.title}
                </h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {item.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Contact Form */}
      <Card className="border-border overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary/80 to-primary/20" />
        <CardContent className="pt-6 pb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            <h2 className="text-lg font-semibold font-heading">
              First, tell us who you are
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mb-6 ml-11">
            {isAuthenticated
              ? "We've filled in your account details. Confirm or edit them before continuing."
              : "We use your email to match you with an existing account. If you're a new client, we'll set things up for you."}
          </p>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                        Full Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Your full name"
                          className="h-11"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                        Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="you@company.com"
                          className="h-11"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                        Company
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Your company name"
                          className="h-11"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="url"
                          placeholder="https://yourcompany.com"
                          className="h-11"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="sm:max-w-[calc(50%-0.625rem)]">
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="+44 7XXX XXXXXX"
                        className="h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {turnstileSiteKey ? (
                <TurnstileWidget
                  siteKey={turnstileSiteKey}
                  action="intake_contact"
                  onTokenChange={setTurnstileToken}
                  resetKey={turnstileResetKey}
                />
              ) : null}

              {turnstileRequired && !turnstileConfigured ? (
                <p className="text-sm text-destructive">
                  Verification is temporarily unavailable. Please try again later.
                </p>
              ) : null}

              {submitError ? (
                <p className="text-sm text-destructive">{submitError}</p>
              ) : null}

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-3">
                <Button
                  type="submit"
                  size="lg"
                  className={`${intakeStackedPrimaryCtaClassName} sm:min-w-[200px] active-elevate-2`}
                  disabled={submitting || (turnstileRequired && !turnstileConfigured)}
                >
                  {submitting ? "Continuing..." : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                {!isAuthenticated ? (
                  <p className="text-xs text-muted-foreground">
                    Already have an account?{" "}
                    <Link
                      href="/login"
                      className="text-primary hover:underline font-medium inline-flex items-center gap-1"
                    >
                      <LogIn className="w-3 h-3" />
                      Sign in
                    </Link>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Signed in — contact details loaded from your account.
                  </p>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
